import { useState, useEffect } from 'react';

import {
  IconX,
  IconMail,
  IconPhone,
  IconAlertCircle,
  IconCalendarStats,
} from '@tabler/icons-react';

import CustomImage from '@/components/CustomImage';
import CustomModal from '@/components/CustomModal';
import { ESubscriptionStatus } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';
import useCommonStore from '@/store/useCommonStore';

interface IParentDetails {
  id: string;
  name: string;
  image: string;
  email: string;
  phone: string;
  status: ESubscriptionStatus;
  license_key: string;
  license_expiry: string;
  last_login: string;
}

interface IParentDetailsModalProps {
  setIsOpen: (open: boolean) => void;
  parentId: string | null;
  onRenewClick: () => void;
}

const ParentDetailsModal = ({
  setIsOpen,
  parentId,
  onRenewClick,
}: IParentDetailsModalProps) => {
  const { formatText } = useFormatText();
  const { setLoadingModal } = useCommonStore();
  const [parent, setParent] = useState<IParentDetails | null>(null);
  const [showWarning, setShowWarning] = useState(true);

  useEffect(() => {
    if (!parentId) return;

    setLoadingModal(true);
    // Simulating API call
    setTimeout(() => {
      setParent({
        id: parentId,
        name: 'Fatima Hassan',
        image: 'https://randomuser.me/api/portraits/women/1.jpg',
        email: 'fatima1hasan@.com',
        phone: '+965 12 345678',
        status: ESubscriptionStatus.ACTIVE,
        license_key: 'BMS-PREM-2024-XK9L',
        license_expiry: 'Dec 31, 2025',
        last_login: 'Dec 22, 2025',
      });
      setLoadingModal(false);
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId]);

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      heading={formatText('report-analytics.license.parent-details.title')}
      position="right"
      className="max-w-150"
      isFooter
      confirmButtonProps={{
        label: formatText(
          'report-analytics.license.parent-details.renew-license'
        ),
        onClick: onRenewClick,
        className: 'bg-purple-600 hover:bg-purple-700 h-10 px-6',
      }}
      cancelButtonProps={{
        label: formatText('common.actions.cancel'),
        onClick: () => setIsOpen(false),
        variant: 'link',
        className: 'text-neutral-primary!',
      }}
    >
      <div className="flex flex-col gap-8 w-full h-full">
        {/* Profile Section */}
        <div className="flex flex-col items-center gap-3">
          <CustomImage
            src={parent?.image || ''}
            alt={parent?.name || ''}
            width={96}
            height={96}
            className="rounded-full border border-neutral-100"
          />
          <h3 className="text-2xl font-bold text-neutral-primary leading-tight">
            {parent?.name}
          </h3>
          <div className="flex gap-2">
            <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-xs font-bold font-mona-sans">
              {formatText('report-analytics.license.parent-details.parent-tag')}
            </span>
            <span className="px-3 py-1 bg-success-light-50 text-success-secondary rounded-full text-xs font-bold font-mona-sans">
              {formatText('common.status.active')}
            </span>
          </div>
        </div>

        {/* Contact Info */}
        <div className="grid grid-cols-2 gap-6 pt-2">
          <div className="flex items-start gap-2.5">
            <div className="text-neutral-secondary">
              <IconMail size={20} color="#717784" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm text-neutral-secondary font-light">
                {formatText('common.email')}
              </span>
              <span className="text-base font-semibold text-neutral-primary font-mona-sans">
                {parent?.email}
              </span>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <div className="text-neutral-secondary">
              <IconPhone size={20} color="#717784" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm text-neutral-secondary font-light">
                {formatText('common.phone-number')}
              </span>
              <span className="text-base font-semibold text-neutral-primary font-mona-sans">
                {parent?.phone}
              </span>
            </div>
          </div>
        </div>

        {/* Important Dates Box */}
        <div className="bg-neutral-light-50 rounded-2xl p-6 flex flex-col gap-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-default rounded-lg text-white">
              <IconCalendarStats size={20} />
            </div>
            <h4 className="text-lg font-semibold text-neutral-primary">
              {formatText(
                'report-analytics.license.parent-details.important-dates'
              )}
            </h4>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-base text-neutral-secondary font-light">
                {formatText(
                  'report-analytics.license.parent-details.license-key'
                )}
                :
              </span>
              <span className="px-3 py-1 bg-white rounded-md text-base font-semibold text-neutral-primary">
                {parent?.license_key}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-base text-neutral-secondary font-light">
                {formatText(
                  'report-analytics.license.parent-details.license-expiry'
                )}
                :
              </span>
              <span className="text-base font-semibold text-neutral-primary">
                {parent?.license_expiry}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-base text-neutral-secondary font-light">
                {formatText(
                  'report-analytics.license.parent-details.last-login'
                )}
                :
              </span>
              <span className="text-base font-semibold text-neutral-primary">
                {parent?.last_login}
              </span>
            </div>
          </div>
        </div>

        {/* Warning Alert */}
        {showWarning && (
          <div className="bg-warning-light-50 border border-warning-light-100 rounded-xl p-4 flex items-center justify-between gap-3 mt-auto">
            <div className="flex items-center gap-3">
              <IconAlertCircle className="text-warning-secondary" size={24} />
              <span className="text-base font-semibold text-neutral-primary">
                {formatText(
                  'report-analytics.license.parent-details.expiring-soon-msg'
                )}
              </span>
            </div>
            <button
              onClick={() => setShowWarning(false)}
              className="text-neutral-secondary hover:text-neutral-primary transition-colors"
            >
              <IconX size={24} color="#99A0AE" />
            </button>
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default ParentDetailsModal;
